import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import SignaturePad from '../components/SignaturePad';
import {
    Plus, Palmtree, CalendarIcon, Users, Check, X, Clock,
    FileText, ChevronLeft, ChevronRight, Cake, PenLine, Trash2
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, getDay, isSameMonth } from 'date-fns';
import { de } from 'date-fns/locale';

const VacationPage = () => {
    const { api, user } = useAuth();
    const [vacations, setVacations] = useState([]);
    const [users, setUsers] = useState([]);
    const [calendarData, setCalendarData] = useState({ vacations: [], birthdays: [] });
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('request');
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [selectedVacation, setSelectedVacation] = useState(null);
    const [employeeSignature, setEmployeeSignature] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [chefSignature, setChefSignature] = useState(null);
    const [formData, setFormData] = useState({
        start_date: '',
        end_date: '',
        reason: '',
        substitute_id: '',
        notes: ''
    });

    const fetchData = async () => {
        try {
            const [vacationsRes, usersRes, calendarRes] = await Promise.all([
                api.get('/vacations'),
                api.get('/users'),
                api.get('/vacations/calendar', { 
                    params: { 
                        month: selectedMonth.getMonth() + 1, 
                        year: selectedMonth.getFullYear() 
                    } 
                })
            ]);
            setVacations(vacationsRes.data);
            setUsers(usersRes.data);
            setCalendarData(calendarRes.data);
        } catch (error) {
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedMonth]);

    const handleCreateRequest = async () => {
        if (!employeeSignature) {
            toast.error('Bitte unterschreiben Sie den Antrag');
            return;
        }
        try {
            setSubmitting(true);
            await api.post('/vacations', { ...formData, employee_signature: employeeSignature });
            toast.success('Urlaubsantrag eingereicht');
            setShowRequestModal(false);
            setFormData({ start_date: '', end_date: '', reason: '', substitute_id: '', notes: '' });
            setEmployeeSignature(null);
            fetchData();
        } catch (error) {
            if (error.response?.status === 409) {
                toast.error('Ein Antrag fuer diesen Zeitraum existiert bereits');
            } else {
                toast.error('Fehler beim Einreichen');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const openApproveModal = (vacation) => {
        setSelectedVacation(vacation);
        setChefSignature(null);
        setShowApproveModal(true);
    };

    const handleApproveWithSignature = async () => {
        if (!selectedVacation) return;
        if (!chefSignature) {
            toast.error('Bitte unterschreiben Sie die Genehmigung');
            return;
        }
        try {
            await api.put(`/vacations/${selectedVacation.id}/approve`, { chef_signature: chefSignature });
            toast.success('Urlaub genehmigt');
            setShowApproveModal(false);
            setSelectedVacation(null);
            setChefSignature(null);
            fetchData();
        } catch (error) {
            toast.error('Fehler');
        }
    };

    const handleApprove = async (vacationId) => {
        try {
            await api.put(`/vacations/${vacationId}/approve`);
            toast.success('Urlaub genehmigt');
            fetchData();
        } catch (error) {
            toast.error('Fehler');
        }
    };

    const handleReject = async (vacationId) => {
        try {
            await api.put(`/vacations/${vacationId}/reject`);
            toast.success('Urlaub abgelehnt');
            fetchData();
        } catch (error) {
            toast.error('Fehler');
        }
    };

    const handleDeleteVacation = async (vacationId) => {
        if (!window.confirm('Urlaub wirklich löschen? Die Urlaubstage werden gutgeschrieben und alle verbundenen Kalendereinträge und Zeitbuchungen werden entfernt.')) {
            return;
        }
        try {
            const res = await api.delete(`/vacations/${vacationId}`);
            toast.success(`Urlaub gelöscht. ${res.data.days_credited || 0} Tage gutgeschrieben.`);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const myVacations = vacations.filter(v => v.user_id === user?.id);
    const pendingRequests = vacations.filter(v => v.status === 'pending');
    const approvedVacations = vacations.filter(v => v.status === 'approved');

    // Calendar helpers
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const startDay = getDay(monthStart);
    const adjustedStartDay = startDay === 0 ? 6 : startDay - 1; // Monday start

    const getDayInfo = (day) => {
        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
        // Get ALL vacations for this day (not just the first one) — skip weekends
        const vacationsOnDay = isWeekend ? [] : (calendarData.vacations?.filter(v => {
            const start = new Date(v.start_date);
            const end = new Date(v.end_date);
            return day >= start && day <= end;
        }) || []);
        // Get birthdays for this day
        const birthdaysOnDay = calendarData.birthdays?.filter(b => b.day === day.getDate()) || [];
        return { vacations: vacationsOnDay, birthdays: birthdaysOnDay, isWeekend };
    };

    const [selectedDayInfo, setSelectedDayInfo] = useState(null);
    const [showDayDetailModal, setShowDayDetailModal] = useState(false);

    const openDayDetail = (day, info) => {
        if (info.vacations.length > 0 || info.birthdays.length > 0) {
            setSelectedDayInfo({ day, ...info });
            setShowDayDetailModal(true);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8" data-testid="vacation-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        🌴 Urlaub
                    </h1>
                    <p className="text-muted-foreground">
                        Resturlaub: <span className="font-semibold text-emerald-500">{user?.remaining_vacation_days || 0} Tage</span>
                    </p>
                </div>
                <Button onClick={() => setShowRequestModal(true)} data-testid="request-vacation-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Urlaub beantragen
                </Button>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList>
                    <TabsTrigger value="request" data-testid="tab-request">Meine Anträge</TabsTrigger>
                    <TabsTrigger value="calendar" data-testid="tab-calendar">Team-Kalender</TabsTrigger>
                    {user?.role === 'chef' && (
                        <TabsTrigger value="pending" data-testid="tab-pending">
                            Genehmigungen
                            {pendingRequests.length > 0 && (
                                <Badge className="ml-2" variant="destructive">{pendingRequests.length}</Badge>
                            )}
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="overview" data-testid="tab-overview">Übersicht</TabsTrigger>
                </TabsList>

                {/* My Requests */}
                <TabsContent value="request">
                    <Card className="shadow-soft">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <FileText className="w-5 h-5" />
                                Meine Urlaubsanträge
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {myVacations.length > 0 ? (
                                <div className="space-y-3">
                                    {myVacations.map(v => (
                                        <motion.div
                                            key={v.id}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                                            data-testid={`vacation-${v.id}`}
                                        >
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                                                    <span className="font-medium">
                                                        {format(new Date(v.start_date), 'd. MMM', { locale: de })} - {format(new Date(v.end_date), 'd. MMM yyyy', { locale: de })}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    {v.days_count} Tage {v.reason && `• ${v.reason}`}
                                                </p>
                                                {v.substitute_name && (
                                                    <p className="text-xs text-muted-foreground">
                                                        Vertretung: {v.substitute_name}
                                                    </p>
                                                )}
                                            </div>
                                            <Badge variant={
                                                v.status === 'approved' ? 'default' :
                                                v.status === 'pending' ? 'secondary' : 'destructive'
                                            }>
                                                {v.status === 'approved' ? '✅ Genehmigt' :
                                                 v.status === 'pending' ? '⏳ Ausstehend' : '❌ Abgelehnt'}
                                            </Badge>
                                        </motion.div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Palmtree className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>Noch keine Urlaubsanträge</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Team Calendar */}
                <TabsContent value="calendar">
                    <Card className="shadow-soft">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                    <Users className="w-5 h-5" />
                                    Team-Kalender
                                </CardTitle>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="icon" onClick={() => setSelectedMonth(subMonths(selectedMonth, 1))}>
                                        <ChevronLeft className="w-4 h-4" />
                                    </Button>
                                    <span className="font-medium min-w-[150px] text-center">
                                        {format(selectedMonth, 'MMMM yyyy', { locale: de })}
                                    </span>
                                    <Button variant="outline" size="icon" onClick={() => setSelectedMonth(addMonths(selectedMonth, 1))}>
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {/* Calendar Grid */}
                            <div className="grid grid-cols-7 gap-1">
                                {/* Weekday headers */}
                                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                                    <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                                        {day}
                                    </div>
                                ))}
                                
                                {/* Empty cells for start offset */}
                                {Array.from({ length: adjustedStartDay }).map((_, i) => (
                                    <div key={`empty-${i}`} className="aspect-square" />
                                ))}
                                
                                {/* Days */}
                                {daysInMonth.map(day => {
                                    const info = getDayInfo(day);
                                    const { vacations, birthdays } = info;
                                    const isToday = isSameDay(day, new Date());
                                    const hasEvents = vacations.length > 0 || birthdays.length > 0;
                                    
                                    return (
                                        <button
                                            key={day.toISOString()}
                                            onClick={() => openDayDetail(day, info)}
                                            disabled={!hasEvents}
                                            className={`aspect-square flex flex-col items-center justify-center rounded-xl text-sm transition-all relative
                                                ${hasEvents ? 'cursor-pointer hover:ring-2 hover:ring-primary/50' : ''}
                                                ${isToday ? 'bg-primary text-primary-foreground font-semibold' : ''}
                                                ${vacations.length > 0 ? 'bg-emerald-500/20' : ''}
                                                ${vacations.length > 1 ? 'bg-emerald-500/40' : ''}
                                                ${birthdays.length > 0 ? 'ring-2 ring-pink-500 ring-offset-1 ring-offset-background' : ''}
                                            `}
                                        >
                                            <span>{format(day, 'd')}</span>
                                            {vacations.length > 0 && (
                                                <div className="text-[8px] text-emerald-700 dark:text-emerald-300 truncate max-w-full px-0.5 leading-tight">
                                                    {vacations.length === 1 ? (
                                                        <span>{vacations[0].user_name?.split(' ')[0]}</span>
                                                    ) : (
                                                        <span className="font-semibold">{vacations.length}x 🌴</span>
                                                    )}
                                                </div>
                                            )}
                                            {birthdays.length > 0 && (
                                                <div className="absolute top-0.5 right-0.5 flex">
                                                    <Cake className="w-3 h-3 text-pink-500" />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            
                            {/* Legend */}
                            <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-6 text-xs md:text-sm">
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded bg-emerald-500/20" />
                                    <span className="text-muted-foreground">1 Person Urlaub</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded bg-emerald-500/40" />
                                    <span className="text-muted-foreground">2+ Urlaube</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded ring-2 ring-pink-500" />
                                    <span className="text-muted-foreground">Geburtstag</span>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">Klicke auf einen Tag für Details</p>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Pending Approvals (Chef only) */}
                {user?.role === 'chef' && (
                    <TabsContent value="pending">
                        <Card className="shadow-soft">
                            <CardHeader>
                                <CardTitle>Ausstehende Genehmigungen</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {pendingRequests.length > 0 ? (
                                    <div className="space-y-3">
                                        {pendingRequests.map(v => (
                                            <motion.div
                                                key={v.id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                                                data-testid={`pending-${v.id}`}
                                            >
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <Avatar className="w-8 h-8">
                                                            <AvatarFallback className="bg-primary text-white text-xs">
                                                                {v.user_name?.split(' ').map(n => n[0]).join('')}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div>
                                                            <p className="font-medium">{v.user_name}</p>
                                                            <p className="text-sm text-muted-foreground">
                                                                {format(new Date(v.start_date), 'd. MMM', { locale: de })} - {format(new Date(v.end_date), 'd. MMM yyyy', { locale: de })} ({v.days_count} Tage)
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {v.reason && <p className="text-sm mt-2">{v.reason}</p>}
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline"
                                                        onClick={() => handleReject(v.id)}
                                                        data-testid={`reject-${v.id}`}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                    <Button 
                                                        size="sm"
                                                        onClick={() => openApproveModal(v)}
                                                        data-testid={`approve-${v.id}`}
                                                    >
                                                        <PenLine className="w-4 h-4 mr-1" />
                                                        Genehmigen
                                                    </Button>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-center py-8 text-muted-foreground">
                                        Keine ausstehenden Anträge
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}

                {/* Overview */}
                <TabsContent value="overview">
                    <div className="grid md:grid-cols-2 gap-6">
                        <Card className="shadow-soft">
                            <CardHeader>
                                <CardTitle>Meine Urlaubstage</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Gesamter Urlaub</span>
                                        <span className="font-semibold">{user?.total_vacation_days || 0} Tage</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Verbraucht</span>
                                        <span className="font-semibold">{(user?.total_vacation_days || 0) - (user?.remaining_vacation_days || 0)} Tage</span>
                                    </div>
                                    <div className="flex justify-between text-lg">
                                        <span className="font-medium">Verbleibend</span>
                                        <span className="font-bold text-emerald-500">{user?.remaining_vacation_days || 0} Tage</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="shadow-soft">
                            <CardHeader>
                                <CardTitle>Genehmigte Urlaube</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {approvedVacations.length > 0 ? (
                                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                        {approvedVacations.map(v => (
                                            <div key={v.id} className="flex items-center gap-3 p-2 rounded-lg bg-emerald-500/10 group">
                                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-600 text-xs font-medium">
                                                    {v.user_name?.charAt(0)}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium">{v.user_name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {format(new Date(v.start_date), 'd. MMM', { locale: de })} - {format(new Date(v.end_date), 'd. MMM', { locale: de })}
                                                        <span className="ml-2 text-emerald-600">({v.days_count} Tage)</span>
                                                    </p>
                                                </div>
                                                {user?.role === 'chef' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                                        onClick={() => handleDeleteVacation(v.id)}
                                                        data-testid={`delete-vacation-${v.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-center text-muted-foreground py-4">
                                        Keine genehmigten Urlaube
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Request Modal */}
            <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
                <DialogContent data-testid="vacation-request-modal">
                    <DialogHeader>
                        <DialogTitle>Urlaub beantragen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Von *</Label>
                                <Input
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                    data-testid="vacation-start"
                                />
                            </div>
                            <div>
                                <Label>Bis *</Label>
                                <Input
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                    data-testid="vacation-end"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Grund</Label>
                            <Input
                                value={formData.reason}
                                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                placeholder="z.B. Familienurlaub"
                                data-testid="vacation-reason"
                            />
                        </div>
                        <div>
                            <Label>Vertretung</Label>
                            <Select value={formData.substitute_id} onValueChange={(v) => setFormData({ ...formData, substitute_id: v })}>
                                <SelectTrigger data-testid="vacation-substitute">
                                    <SelectValue placeholder="Vertretung wählen" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.filter(u => u.id !== user?.id).map(u => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Notizen</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Weitere Hinweise..."
                                data-testid="vacation-notes"
                            />
                        </div>

                        {/* Employee Signature */}
                        <div>
                            <Label className="flex items-center gap-2">
                                <PenLine className="w-4 h-4" />
                                Unterschrift Mitarbeiter *
                            </Label>
                            {employeeSignature ? (
                                <div className="mt-2 space-y-2">
                                    <div className="border rounded-lg p-2 bg-muted/30">
                                        <img src={employeeSignature} alt="Unterschrift" className="max-h-24 mx-auto" />
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => setEmployeeSignature(null)}
                                        className="w-full"
                                    >
                                        Neu unterschreiben
                                    </Button>
                                </div>
                            ) : (
                                <div className="mt-2">
                                    <SignaturePad onSave={setEmployeeSignature} width={380} height={120} />
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRequestModal(false)}>Abbrechen</Button>
                        <Button 
                            onClick={handleCreateRequest} 
                            disabled={!formData.start_date || !formData.end_date || !employeeSignature || submitting}
                            data-testid="submit-vacation-button"
                        >
                            {submitting ? 'Wird eingereicht...' : 'Einreichen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Approve Modal with Chef Signature */}
            <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
                <DialogContent data-testid="vacation-approve-modal">
                    <DialogHeader>
                        <DialogTitle>Urlaubsantrag genehmigen</DialogTitle>
                    </DialogHeader>
                    {selectedVacation && (
                        <div className="space-y-4">
                            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                                <p className="font-medium">{selectedVacation.user_name}</p>
                                <p className="text-sm text-muted-foreground">
                                    {format(new Date(selectedVacation.start_date), 'd. MMM yyyy', { locale: de })} - {format(new Date(selectedVacation.end_date), 'd. MMM yyyy', { locale: de })}
                                </p>
                                <p className="text-sm">{selectedVacation.days_count} Tage</p>
                                {selectedVacation.reason && (
                                    <p className="text-sm">Grund: {selectedVacation.reason}</p>
                                )}
                            </div>

                            {/* Employee Signature Display */}
                            {selectedVacation.employee_signature && (
                                <div>
                                    <Label className="text-sm text-muted-foreground">Unterschrift Mitarbeiter</Label>
                                    <div className="border rounded-lg p-2 bg-white mt-1">
                                        <img src={selectedVacation.employee_signature} alt="Mitarbeiter-Unterschrift" className="max-h-16 mx-auto" />
                                    </div>
                                </div>
                            )}

                            {/* Chef Signature */}
                            <div>
                                <Label className="flex items-center gap-2">
                                    <PenLine className="w-4 h-4" />
                                    Unterschrift Chef *
                                </Label>
                                {chefSignature ? (
                                    <div className="mt-2 space-y-2">
                                        <div className="border rounded-lg p-2 bg-muted/30">
                                            <img src={chefSignature} alt="Unterschrift" className="max-h-24 mx-auto" />
                                        </div>
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            onClick={() => setChefSignature(null)}
                                            className="w-full"
                                        >
                                            Neu unterschreiben
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="mt-2">
                                        <SignaturePad onSave={setChefSignature} width={380} height={120} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowApproveModal(false)}>Abbrechen</Button>
                        <Button 
                            onClick={handleApproveWithSignature}
                            disabled={!chefSignature}
                        >
                            <Check className="w-4 h-4 mr-2" />
                            Genehmigen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Day Detail Modal */}
            <Dialog open={showDayDetailModal} onOpenChange={setShowDayDetailModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CalendarIcon className="w-5 h-5" />
                            {selectedDayInfo?.day && format(selectedDayInfo.day, 'd. MMMM yyyy', { locale: de })}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedDayInfo && (
                        <div className="space-y-4">
                            {/* Vacations */}
                            {selectedDayInfo.vacations?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2 text-emerald-600">
                                        <Palmtree className="w-4 h-4" />
                                        Urlaub ({selectedDayInfo.vacations.length})
                                    </h4>
                                    <div className="space-y-2">
                                        {selectedDayInfo.vacations.map(v => (
                                            <div key={v.id} className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10">
                                                <Avatar className="w-10 h-10">
                                                    <AvatarFallback 
                                                        style={{ backgroundColor: v.user_color || '#10b981' }}
                                                        className="text-white text-sm"
                                                    >
                                                        {v.user_name?.split(' ').map(n => n[0]).join('')}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex-1">
                                                    <p className="font-medium">{v.user_name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {format(new Date(v.start_date), 'd. MMM', { locale: de })} - {format(new Date(v.end_date), 'd. MMM', { locale: de })}
                                                    </p>
                                                </div>
                                                <Badge variant={v.status === 'approved' ? 'default' : 'secondary'}>
                                                    {v.status === 'approved' ? 'Genehmigt' : 'Ausstehend'}
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Birthdays */}
                            {selectedDayInfo.birthdays?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2 text-pink-500">
                                        <Cake className="w-4 h-4" />
                                        Geburtstag ({selectedDayInfo.birthdays.length})
                                    </h4>
                                    <div className="space-y-2">
                                        {selectedDayInfo.birthdays.map((b, i) => (
                                            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-pink-500/10">
                                                <Avatar className="w-10 h-10">
                                                    <AvatarFallback 
                                                        style={{ backgroundColor: b.profile_color || '#ec4899' }}
                                                        className="text-white text-sm"
                                                    >
                                                        {b.name?.split(' ').map(n => n[0]).join('')}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <p className="font-medium">{b.name}</p>
                                                    <p className="text-xs text-muted-foreground">🎂 Feiert Geburtstag!</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={() => setShowDayDetailModal(false)}>Schließen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default VacationPage;
